import './Breadcrumbs.scss';

import * as React from 'react';

import { translate } from '~l10n/client';
import { IconButton } from '~shared/components/buttons';
import { IconEllipsis } from '~shared/components/icons/standard';
import { LinkMenu } from '~shared/components/LinkMenu';

export interface Crumb {
  label: string;
  href: string;
}

interface Props {
  /** Full trail, ordered from root to the current page */
  crumbs: Crumb[];
  /**
   * Omit the current-page crumb, for pages whose heading already shows the
   * current name (e.g. the quiz editor).
   */
  hideCurrent?: boolean;
}

/** Trails longer than this collapse into: root / … / parent / current */
const MAX_VISIBLE_CRUMBS = 4;

export const Breadcrumbs = ({ crumbs, hideCurrent = false }: Props) => {
  const trail = hideCurrent ? crumbs.slice(0, -1) : crumbs;

  if (trail.length === 0) {
    return null;
  }

  const collapsed = trail.length > MAX_VISIBLE_CRUMBS;
  const visible = collapsed
    ? [trail[0], ...trail.slice(trail.length - 2)]
    : trail;
  const overflow = collapsed ? trail.slice(1, trail.length - 2) : [];

  return (
    <nav className="breadcrumbs min-w-0" aria-label={translate('Breadcrumbs')}>
      <ol className="flex items-center gap-2 min-w-0">
        {visible.map((crumb, index) => {
          const isCurrent = !hideCurrent && index === visible.length - 1;
          return (
            <React.Fragment key={`${crumb.href}-${index}`}>
              {index > 0 && <Separator />}
              {collapsed && index === 1 && (
                <>
                  <li className="flex-shrink-0">
                    <LinkMenu links={overflow} direction="bottom-start">
                      {/* onClick makes IconButton render a focusable <button>;
                          the menu toggle itself is handled by LinkMenu's Popper */}
                      <IconButton
                        appearance="ghost"
                        onClick={() => undefined}
                        aria-label={translate('Show all folders')}
                      >
                        <IconEllipsis />
                      </IconButton>
                    </LinkMenu>
                  </li>
                  <Separator />
                </>
              )}
              <li className="breadcrumbs-crumb">
                {isCurrent ? (
                  <span
                    aria-current="page"
                    title={crumb.label}
                    className="block truncate py-2 font-sans text-sm font-600 leading-8 text-blue-900"
                  >
                    {crumb.label}
                  </span>
                ) : (
                  <a
                    href={crumb.href}
                    title={crumb.label}
                    className="block truncate py-2 font-sans text-sm font-600 leading-8 text-grey-900 no-underline hocus:text-blue-900"
                  >
                    {crumb.label}
                  </a>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

const Separator = () => (
  <li
    aria-hidden="true"
    className="flex-shrink-0 font-sans text-sm font-600 leading-8 text-grey-900"
  >
    /
  </li>
);
